import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { BuddyRead as BuddyReadType, BuddyReadMember } from "@/types/buddyReads";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Calendar, Users, BookOpen } from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";

export default function BuddyRead() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [buddyRead, setBuddyRead] = useState<BuddyReadType | null>(null);
  const [loading, setLoading] = useState(true);
  const [newPage, setNewPage] = useState("");

  useEffect(() => {
    if (!id || !user) return;

    fetchBuddyRead();
    setupRealtimeSubscription();
  }, [id, user]);

  const fetchBuddyRead = async () => {
    if (!id || !user) return;

    try {
      // Fetch buddy read details
      const { data: buddyReadData, error: readError } = await supabase
        .from('buddy_reads')
        .select(`
          *,
          book:books(id, title, authors, cover_url, page_count)
        `)
        .eq('id', id)
        .single();

      if (readError) throw readError;

      // Fetch members
      const { data: membersData, error: membersError } = await supabase
        .from('buddy_read_members')
        .select(`
          *,
          user:profiles(id, username, name, image)
        `)
        .eq('buddy_read_id', id)
        .eq('status', 'accepted');

      if (membersError) throw membersError;

      setBuddyRead({
        ...buddyReadData,
        status: buddyReadData.status as 'active' | 'completed' | 'abandoned',
        book: Array.isArray(buddyReadData.book) ? buddyReadData.book[0] : buddyReadData.book,
        members: membersData?.map(m => ({
          ...m,
          status: m.status as 'invited' | 'accepted' | 'declined',
          user: Array.isArray(m.user) ? m.user[0] : m.user
        })) || []
      });
    } catch (error: any) {
      console.error('Error fetching buddy read:', error);
      toast({
        title: "Error loading buddy read",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscription = () => {
    if (!id) return;

    const channel = supabase
      .channel(`buddy-read-${id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'buddy_read_members',
          filter: `buddy_read_id=eq.${id}`
        },
        () => {
          fetchBuddyRead();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const handleUpdateProgress = async () => {
    if (!user || !id || !newPage) return;

    const pageNum = parseInt(newPage);
    if (isNaN(pageNum) || pageNum < 0) {
      toast({
        title: "Invalid page number",
        variant: "destructive"
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('buddy_read_members')
        .update({
          current_page: pageNum,
          last_updated: new Date().toISOString()
        })
        .eq('buddy_read_id', id)
        .eq('user_id', user.id);

      if (error) throw error;

      toast({
        title: "Progress updated!",
        description: `You're on page ${pageNum}`
      });

      setNewPage("");
    } catch (error: any) {
      console.error('Error updating progress:', error);
      toast({
        title: "Error updating progress",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="container max-w-4xl mx-auto py-8">
        <p>Loading...</p>
      </div>
    );
  }

  if (!buddyRead) {
    return (
      <div className="container max-w-4xl mx-auto py-8">
        <p>Buddy read not found</p>
      </div>
    );
  }

  const myMember = buddyRead.members?.find(m => m.user_id === user?.id);
  const totalPages = buddyRead.book?.page_count || 0;

  return (
    <div className="container max-w-4xl mx-auto py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">
            {buddyRead.title || buddyRead.book?.title}
          </h1>
          {buddyRead.title && (
            <p className="text-muted-foreground">{buddyRead.book?.title}</p>
          )}
        </div>
      </div>

      {/* Book Info */}
      <Card>
        <CardContent className="p-6">
          <div className="flex gap-6">
            <img
              src={buddyRead.book?.cover_url || '/placeholder.svg'}
              alt={buddyRead.book?.title}
              className="w-32 h-48 object-cover rounded shadow-md"
            />
            <div className="flex-1 space-y-4">
              <div>
                <h2 className="text-xl font-semibold">{buddyRead.book?.title}</h2>
                <p className="text-muted-foreground">
                  by {buddyRead.book?.authors?.join(', ')}
                </p>
              </div>

              <div className="flex gap-6 text-sm">
                {totalPages > 0 && (
                  <div className="flex items-center gap-2">
                    <BookOpen className="h-4 w-4 text-muted-foreground" />
                    <span>{totalPages} pages</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span>{buddyRead.members?.length} readers</span>
                </div>
                {buddyRead.target_end_date && (
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>
                      Target: {format(new Date(buddyRead.target_end_date), 'MMM d, yyyy')}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Update Progress */}
      <Card>
        <CardHeader>
          <CardTitle>Update Your Progress</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <Label htmlFor="page">Current Page</Label>
              <Input
                id="page"
                type="number"
                placeholder={`Current: ${myMember?.current_page || 0}`}
                value={newPage}
                onChange={(e) => setNewPage(e.target.value)}
                min="0"
                max={totalPages}
              />
            </div>
            <Button onClick={handleUpdateProgress} disabled={!newPage}>
              Update Progress
            </Button>
          </div>

          {myMember && totalPages > 0 && (
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Your progress</span>
                <span className="font-medium">
                  {Math.round((myMember.current_page / totalPages) * 100)}%
                </span>
              </div>
              <Progress value={(myMember.current_page / totalPages) * 100} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Members Progress */}
      <Card>
        <CardHeader>
          <CardTitle>Reading Progress</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {buddyRead.members?.map((member) => {
            const progress = totalPages > 0 ? (member.current_page / totalPages) * 100 : 0;
            return (
              <div key={member.id} className="space-y-2">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={member.user?.image} />
                    <AvatarFallback>
                      {member.user?.username?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <p className="font-medium">
                      {member.user?.name || member.user?.username}
                    </p>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        Page {member.current_page} of {totalPages}
                      </span>
                      <span className="text-muted-foreground">
                        {Math.round(progress)}%
                      </span>
                    </div>
                  </div>
                </div>
                <Progress value={progress} />
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
